import { FormEvent, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { AdminApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { Field, PageHeader, SimBadge, Spinner, StatusBadge } from "../../components/common";
import { DataTable } from "../../components/DataTable";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const TYPES = ["BLS Ambulance", "ALS Ambulance", "Patient Transport Van"];

function VehicleDialog({ vehicle, responders, onClose }: { vehicle: any; responders: any[]; onClose: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState<any>({ plate: vehicle?.plate || "", type: vehicle?.type || TYPES[0], status: vehicle?.status || "in_service", responder_id: vehicle?.responder_id || "" });
  const save = useMutation({
    mutationFn: () => {
      const body = { ...f, responder_id: f.responder_id || null };
      return vehicle ? AdminApi.updateVehicle(vehicle.id, body) : AdminApi.createVehicle(body);
    },
    onSuccess: () => { toast.success("Vehicle saved"); qc.invalidateQueries({ queryKey: ["admin"] }); qc.invalidateQueries({ queryKey: ["dispatch"] }); onClose(); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const submit = (e: FormEvent) => { e.preventDefault(); save.mutate(); };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="vehicle-dialog">
        <DialogHeader><DialogTitle>{vehicle ? "Edit vehicle" : "Add vehicle"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Plate number"><input className="fp-input" required value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value })} data-testid="vehicle-plate-input" /></Field>
          <Field label="Type"><select className="fp-input" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="vehicle-type-select">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></Field>
          <Field label="Status"><select className="fp-input" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="vehicle-status-select">
            <option value="in_service">In service</option><option value="maintenance">Maintenance</option><option value="retired">Retired</option></select></Field>
          <Field label="Assigned responder"><select className="fp-input" value={f.responder_id} onChange={(e) => setF({ ...f, responder_id: e.target.value })} data-testid="vehicle-responder-select">
            <option value="">Unassigned</option>{responders.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></Field>
          <button className="fp-btn-primary w-full" disabled={save.isPending} data-testid="vehicle-save-button">Save</button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminFleet() {
  const [editing, setEditing] = useState<any>(undefined);
  const { data, isLoading } = useQuery({ queryKey: ["admin", "vehicles"], queryFn: AdminApi.vehicles });
  const { data: responders = [] } = useQuery({ queryKey: ["admin", "users", "responder"], queryFn: () => AdminApi.users({ role: "responder" }) });
  const name = (id: string) => responders.find((r) => r.id === id)?.name || "—";
  return (
    <div className="space-y-4">
      <PageHeader title="Fleet" action={<button className="fp-btn-primary" onClick={() => setEditing(null)} data-testid="fleet-add-button"><Plus size={16} /> Add vehicle</button>} />
      {isLoading ? <Spinner /> : (
        <DataTable testId="fleet-table" rows={data || []} columns={[
          { key: "plate", label: "Plate", render: (v) => <span className="flex items-center gap-1.5 font-mono font-semibold">{v.plate}<SimBadge show={v.is_simulated} /></span> },
          { key: "type", label: "Type", render: (v) => v.type },
          { key: "status", label: "Status", render: (v) => <StatusBadge status={v.status === "in_service" ? "active" : "pending"} label={v.status.replace("_", " ")} /> },
          { key: "resp", label: "Responder", render: (v) => name(v.responder_id) },
          { key: "edit", label: "", render: (v) => <button className="fp-btn-secondary min-h-[36px] px-3 text-xs" onClick={() => setEditing(v)} data-testid={`fleet-edit-${v.id}`}>Edit</button> },
        ]} />
      )}
      {editing !== undefined && <VehicleDialog vehicle={editing} responders={responders} onClose={() => setEditing(undefined)} />}
    </div>
  );
}
